import axios from 'axios';

// Use same-origin API calls (no cross-origin needed)
// In production: bridgewayglobalenglish.com/api → proxied to backend by nginx
// In preview: preview-url.emergentagent.com/api → proxied to backend by K8s ingress
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API_URL = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  getMe: () => api.get('/auth/me'),
  updateTimezone: (timezone) => api.patch('/auth/me/timezone', { timezone }),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, new_password) => api.post('/auth/reset-password', { token, new_password }),
  passwordStatus: () => api.get('/auth/password-status'),
  setPassword: (new_password, current_password) => api.post('/auth/set-password', { new_password, current_password }),
  verifyResetToken: (token) => api.get(`/auth/verify-reset-token/${token}`),
  getLockoutStatus: (email) => api.get(`/auth/lockout-status/${email}`),
  getSessionConfig: () => api.get('/auth/session-config'),
  refresh: () => api.post('/auth/refresh'),
  // Super admin only: use the site as 'tutor' or 'student', or back to 'admin'.
  actAs: (role) => api.post('/auth/act-as', { role }),
};

// Tutors
export const tutorsAPI = {
  getTutors: (params) => api.get('/tutors', { params }),
  getTutor: (id) => api.get(`/tutors/${id}`),
  getMyRate: () => api.get('/tutor/rate'),
  setMyRate: (data) => api.put('/tutor/rate', data),
  createProfile: (data) => api.post('/tutors/profile', data),
  getMyProfile: () => api.get('/tutors/profile/me'),
  updateProfile: (data) => api.put('/tutors/profile', data),
  getMyStudents: (params) => api.get('/tutors/my-students', { params }),
  // Trial Video
  getTrialVideo: (tutorId) => api.get(`/tutors/${tutorId}/trial-video`),
  trackVideoView: (tutorId) => api.post(`/tutors/${tutorId}/trial-video/view`),
  trackCtaClick: (tutorId) => api.post(`/tutors/${tutorId}/trial-video/cta-click`),
  getVideoAnalytics: (tutorId) => api.get(`/tutors/${tutorId}/trial-video/analytics`),
  // Trial Lesson Discount
  getTrialEligibility: (tutorId) => api.get(`/tutors/${tutorId}/trial-eligibility`),
  // Tutor's own trial video management
  getMyTrialVideo: () => api.get('/tutors/me/trial-video'),
  submitTrialVideo: (data) => api.post('/tutors/me/trial-video', data),
  updateTrialVideo: (data) => api.put('/tutors/me/trial-video', data),
  deleteTrialVideo: () => api.delete('/tutors/me/trial-video'),
};

// Bookings
export const bookingsAPI = {
  confirmClass: (bookingId) => api.post(`/bookings/${bookingId}/confirm`),
  reschedule: (bookingId, date, startTime) => api.post(`/bookings/${bookingId}/reschedule`, { date, start_time: startTime }),
  createBooking: (data) => api.post('/bookings', data),
  getMyBookings: () => api.get('/bookings/my'),
  getBooking: (id) => api.get(`/bookings/${id}`),
  getMeetingInfo: (bookingId) => api.get(`/bookings/${bookingId}/meeting`),
  startLesson: (bookingId) => api.post(`/bookings/${bookingId}/start-lesson`),
  endLesson: (bookingId) => api.post(`/bookings/${bookingId}/end-lesson`),
  extendLesson: (bookingId, minutes) => api.post(`/bookings/${bookingId}/extend`, { minutes }),
  classroomPresence: (bookingId, clientId, takeover = false, speed = {}) =>
    api.post(`/bookings/${bookingId}/presence`, { client_id: clientId, takeover, ...speed }),
  // Tutor booking management
  getPendingRequests: () => api.get('/bookings/pending-requests'),
  acceptBooking: (bookingId) => api.put(`/bookings/${bookingId}/accept`),
  rejectBooking: (bookingId, reason) => api.put(`/bookings/${bookingId}/reject`, null, { params: { reason } }),
  // Lesson notes
  getLessonNotes: (bookingId) => api.get(`/bookings/${bookingId}/notes`),
  updateLessonNotes: (bookingId, notes, lessonTopic = null) =>
    api.put(`/bookings/${bookingId}/notes`, { notes, lesson_topic: lessonTopic }),
  getStudentNotesHistory: (studentId) => api.get(`/students/${studentId}/notes`),
  // Attendance
  getAttendance: (bookingId) => api.get(`/bookings/${bookingId}/attendance`),
  markAttendance: (bookingId, status, notes) => api.put(`/bookings/${bookingId}/attendance`, { status, notes }),
  // Post-lesson feedback (bidirectional)
  getBookingFeedback: (bookingId) => api.get(`/bookings/${bookingId}/feedback`),
  rateTutor: (bookingId, rating, comment = '') =>
    api.post(`/bookings/${bookingId}/feedback/student-to-tutor`, { rating, comment }),
  encourageStudent: (bookingId, comment, badges = []) =>
    api.post(`/bookings/${bookingId}/feedback/tutor-to-student`, { comment, badges }),
  getStudentEncouragements: (studentId) => api.get(`/students/${studentId}/encouragements`),
};

// Chat
export const chatAPI = {
  getMessages: (bookingId, limit = 50) => api.get(`/chat/messages/${bookingId}`, { params: { limit } }),
  sendMessage: (bookingId, message) => api.post('/chat/messages', { booking_id: bookingId, message }),
  getUnreadCount: () => api.get('/chat/unread-count'),
};

// Payments
export const paymentsAPI = {
  createCheckout: (data) => api.post('/payments/checkout', data),
  getPaymentStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

// Reviews
export const reviewsAPI = {
  createReview: (data) => api.post('/reviews', data),
  getTutorReviews: (tutorId) => api.get(`/reviews/tutor/${tutorId}`),
  getMyReviews: () => api.get('/reviews/mine'),
  replyToReview: (reviewId, reply) => api.post(`/reviews/${reviewId}/reply`, { reply }),
  flagReview: (reviewId, reason) => api.post(`/reviews/${reviewId}/flag`, { reason: reason || null }),
  adminListReviews: (status = 'flagged') => api.get(`/admin/reviews?status=${status}`),
  adminSetVisibility: (reviewId, isHidden, reason) =>
    api.put(`/admin/reviews/${reviewId}/visibility`, { is_hidden: isHidden, reason: reason || null }),
};

// Stats
export const statsAPI = {
  getTutorStats: () => api.get('/stats/tutor'),
  getStudentStats: () => api.get('/stats/student'),
};

// Admin
export const usersAPI = {
  uploadAvatar: (file) => {
    const form = new FormData();
    form.append('file', file);
    return api.post('/users/me/avatar', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteAvatar: () => api.delete('/users/me/avatar'),
  avatars: (ids) => api.get('/users/avatars', { params: { ids: ids.join(',') } }),
};

export const adminAPI = {
  testUpcomingClasses: () => api.get('/admin/test/upcoming-classes'),
  testFinishClass: (id, attended) => api.post(`/admin/test/finish-class/${id}`, { attended }),
  getRefundRequests: () => api.get('/admin/refund-requests'),
  decideRefund: (id, approve, note) => api.post(`/admin/refund-requests/${id}`, { approve, note }),
  funds: () => api.get('/admin/funds'),
  testMoneyResetStatus: () => api.get('/admin/reset-test-money'),
  resetTestMoney: (confirm) => api.post('/admin/reset-test-money', { confirm }),
  wiseTransfers: (status = 'open') => api.get('/admin/wise-transfers', { params: { status } }),
  confirmWiseTransfer: (id, note) => api.post(`/admin/wise-transfers/${id}/confirm`, { note }),
  rejectWiseTransfer: (id, note) => api.post(`/admin/wise-transfers/${id}/reject`, { note }),
  getStudentPaymentSettings: () => api.get('/admin/student-payment-settings'),
  saveStudentPaymentSettings: (data) => api.put('/admin/student-payment-settings', data),
  wiseAutoConfirm: () => api.get('/admin/wise/auto-confirm'),
  connectWiseAutoConfirm: () => api.post('/admin/wise/auto-confirm/connect'),
  getSubscriptions: () => api.get('/admin/subscriptions'),
  testSubscription: (id, action) => api.post(`/admin/subscriptions/${id}/test`, { action }),
  getAllTutors: (status) => api.get('/admin/tutors', { params: status ? { status } : {} }),
  approveTutor: (tutorId, data = {}) => api.put(`/admin/tutors/${tutorId}/approve`, data),
  rejectTutor: (tutorId, reason) => api.put(`/admin/tutors/${tutorId}/reject`, { reason }),
  toggleTutorActive: (tutorId, isActive, reason) =>
    api.put(`/admin/tutors/${tutorId}/toggle-active`, { is_active: isActive, reason: reason || null }),
  getPricingSettings: () => api.get('/admin/pricing-settings'),
  updatePricingSettings: (data) => api.put('/admin/pricing-settings', data),
  updateTutorRateLimits: (limits) => api.put('/admin/tutor-rate-limits', limits),
  setTutorIdVerified: (tutorId, idVerified) =>
    api.put(`/admin/tutors/${tutorId}/verify-id`, { id_verified: idVerified }),
  getAdminStats: () => api.get('/admin/stats'),
  getActivePayoutGateway: () => api.get('/payout-gateway/active'),
  getDashboard: () => api.get('/admin/dashboard'),
  getDropOffBookings: (statusFilter = 'all', limit = 200) =>
    api.get('/admin/bookings/drop-offs', { params: { status_filter: statusFilter, limit } }),
  getReplacementCandidates: (bookingId) => api.get(`/admin/bookings/${bookingId}/replacement-candidates`),
  assignReplacement: (bookingId, newTutorId) =>
    api.post(`/admin/bookings/${bookingId}/assign-replacement`, { new_tutor_id: newTutorId }),
  recordRefund: (bookingId, data) =>
    api.post(`/admin/bookings/${bookingId}/refund`, data),
  listRefunds: (limit = 100) => api.get('/admin/refunds', { params: { limit } }),
  // Student Management
  getStudents: (params) => api.get('/admin/students', { params }),
  // Tutor Photo Management
  uploadTutorPhoto: (tutorId, formData) => api.post(`/tutors/${tutorId}/photo`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  deleteTutorPhoto: (tutorId) => api.delete(`/tutors/${tutorId}/photo`),
  // Package Management
  getPackages: () => api.get('/admin/packages'),
  createPackage: (data) => api.post('/admin/packages', data),
  updatePackage: (packageId, data) => api.put(`/admin/packages/${packageId}`, data),
  togglePackage: (packageId) => api.put(`/admin/packages/${packageId}/toggle`),
  deletePackage: (packageId) => api.delete(`/admin/packages/${packageId}`),
  getStudentPackages: () => api.get('/admin/student-packages'),
  // Admin User Management
  getAdminUsers: () => api.get('/admin/users'),
  createAdminUser: (data) => api.post('/admin/users', data),
  deleteAdminUser: (userId) => api.delete(`/admin/users/${userId}`),
  // All accounts (students, tutors, admins)
  listAccounts: (params) => api.get('/admin/accounts', { params }),
  createAccount: (data) => api.post('/admin/accounts', data),
  updateAccount: (userId, data) => api.patch(`/admin/accounts/${userId}`, data),
  setAccountPassword: (userId, data) => api.post(`/admin/accounts/${userId}/password`, data),
  deleteAccount: (userId) => api.delete(`/admin/accounts/${userId}`),
  staffEmailSettings: () => api.get('/admin/staff-email-settings'),
  me: () => api.get('/admin/me'),
  activity: (params) => api.get('/admin/activity', { params }),
  transactions: (params) => api.get('/admin/transactions', { params }),
  updateStaffEmailSettings: (enforce) => api.put('/admin/staff-email-settings', { enforce }),
  // Trial Video Management
  getPendingTrialVideos: () => api.get('/admin/trial-videos/pending'),
  approveTrialVideo: (tutorId) => api.put(`/admin/trial-videos/${tutorId}/approve`),
  rejectTrialVideo: (tutorId, reason) => 
    api.put(`/admin/trial-videos/${tutorId}/reject`, null, { params: { reason } }),
  // Slot Duration Whitelist & Legacy Cleanup
  getSlotDurationWhitelist: () => api.get('/admin/platform-settings/slot-durations'),
  updateSlotDurationWhitelist: (allowed_slot_durations) =>
    api.put('/admin/platform-settings/slot-durations', { allowed_slot_durations }),
  cleanupLegacySlots: (dryRun = true) =>
    api.post('/admin/cleanup-legacy-slots', null, { params: { dry_run: dryRun } }),
  // Stale Pending Bookings (auto-cancel after 24h unpaid)
  listStalePendingBookings: (hours = 24) =>
    api.get('/admin/stale-pending-bookings', { params: { hours } }),
  sweepStalePendingBookings: () =>
    api.post('/admin/stale-pending-bookings/sweep-now'),
  // Admin-managed Slot Configurations (duration + price + commission)
  listSlotConfigurations: () => api.get('/admin/slot-configurations'),
  createSlotConfiguration: (data) => api.post('/admin/slot-configurations', data),
  updateSlotConfiguration: (id, data) => api.put(`/admin/slot-configurations/${id}`, data),
  deleteSlotConfiguration: (id, hard = false) =>
    api.delete(`/admin/slot-configurations/${id}`, { params: { hard } }),
};

// Public slot configurations (any authenticated user)
export const slotConfigsAPI = {
  list: () => api.get('/slot-configurations'),
};

// Session Packages (Public/Student)
export const packagesAPI = {
  getActivePackages: () => api.get('/packages'),
  purchasePackage: (packageId) => api.post(`/packages/${packageId}/purchase`),
  getMyPackages: () => api.get('/my-packages'),
  redeemPackage: (data) => api.post('/packages/redeem', data),
};

// Availability
export const availabilityAPI = {
  createRule: (data) => api.post('/tutors/availability/rules', data),
  getMyRules: () => api.get('/tutors/availability/rules/me'),
  updateRule: (ruleId, data) => api.put(`/tutors/availability/rules/${ruleId}`, data),
  deleteRule: (ruleId) => api.delete(`/tutors/availability/rules/${ruleId}`),
  createException: (data) => api.post('/tutors/availability/exceptions', data),
  getMyExceptions: () => api.get('/tutors/availability/exceptions/me'),
  deleteException: (exceptionId) => api.delete(`/tutors/availability/exceptions/${exceptionId}`),
  getAvailableSlots: (tutorId, startDate, endDate, userTimezone = null) => {
    const params = { start_date: startDate, end_date: endDate };
    if (userTimezone) params.user_timezone = userTimezone;
    return api.get(`/tutors/${tutorId}/available-slots`, { params });
  },
};

// Scheduling Engine
export const schedulingAPI = {
  getConfig: () => api.get('/scheduling/config'),
  validateSlot: (tutorId, date, startTime, endTime) => 
    api.post('/scheduling/validate-slot', null, { 
      params: { tutor_id: tutorId, date, start_time: startTime, end_time: endTime } 
    }),
};

// Earnings & Payouts (Tutor)
export const earningsAPI = {
  getEarnings: () => api.get('/earnings/tutor'),
  getTransactions: (limit = 50, offset = 0) => 
    api.get('/earnings/transactions', { params: { limit, offset } }),
  requestPayout: (data) => api.post('/payouts/request', data),
  getMyPayoutRequests: () => api.get('/payouts/my-requests'),
  getCashoutMethod: () => api.get('/payouts/method'),
  saveCashoutMethod: (data) => api.put('/payouts/method', data),
  deleteCashoutMethod: () => api.delete('/payouts/method'),
};

// Stripe Connect (Tutor)
export const connectAPI = {
  createAccount: () => api.post('/connect/create-account'),
  getOnboardingLink: () => api.post('/connect/onboarding-link'),
  getStatus: () => api.get('/connect/status'),
  getDashboardLink: () => api.get('/connect/dashboard-link'),
};

// Admin Reports & Payouts
export const reportsAPI = {
  getOverview: () => api.get('/admin/reports/overview'),
  getRevenueReport: (period = 'daily', days = 30) => 
    api.get('/admin/reports/revenue', { params: { period, days } }),
  getTutorPerformance: () => api.get('/admin/reports/tutors'),
  exportReport: (reportType) => 
    api.get('/admin/reports/export', { params: { report_type: reportType }, responseType: 'blob' }),
  // Payout management
  getAllPayouts: (status = null) => 
    api.get('/admin/payouts', { params: status ? { status } : {} }),
  approvePayout: (payoutId) => api.put(`/admin/payouts/${payoutId}/approve`),
  completePayout: (payoutId, adminNotes = null) => 
    api.put(`/admin/payouts/${payoutId}/complete`, null, { params: adminNotes ? { admin_notes: adminNotes } : {} }),
  rejectPayout: (payoutId, reason) => 
    api.put(`/admin/payouts/${payoutId}/reject`, null, { params: { reason } }),
  executeTransfer: (payoutId) => api.post(`/admin/payouts/${payoutId}/transfer`),
  testPayoutRecipients: () => api.get('/admin/payouts/test-recipients'),
  sendTestPayout: (data) => api.post('/admin/payouts/test', data),
  getConnectAccounts: () => api.get('/admin/connect/accounts'),
  getPayoutSummary: () => api.get('/admin/payout-summary'),
  getPayoutSettings: () => api.get('/admin/payout-settings'),
  updatePayoutSettings: (data) => api.put('/admin/payout-settings', data),
  getPayoutGatewayStatus: () => api.get('/admin/payout-gateways/status'),
};

// Learning Materials
export const materialsAPI = {
  // Public/Student endpoints
  getConfig: () => api.get('/materials/config'),
  getMaterials: (level = null, category = null, search = null) => {
    const params = {};
    if (level) params.level = level;
    if (category) params.category = category;
    if (search) params.search = search;
    return api.get('/materials', { params });
  },
  getMaterial: (materialId) => api.get(`/materials/${materialId}`),
  viewMaterialPdf: (materialId) => `${API_URL}/api/materials/${materialId}/view`,
  
  // Admin endpoints
  uploadMaterial: (formData) => api.post('/admin/materials/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getAllMaterialsAdmin: (level = null, category = null, isActive = null) => {
    const params = {};
    if (level) params.level = level;
    if (category) params.category = category;
    if (isActive !== null) params.is_active = isActive;
    return api.get('/admin/materials', { params });
  },
  updateMaterial: (materialId, data) => api.put(`/admin/materials/${materialId}`, data),
  deleteMaterial: (materialId) => api.delete(`/admin/materials/${materialId}`),
  getStats: () => api.get('/materials/stats/summary'),
};

// Booking Holds
export const bookingHoldsAPI = {
  createHold: (data) => api.post('/booking-holds', data),
  releaseHold: (holdId) => api.delete(`/booking-holds/${holdId}`),
  getMyHolds: () => api.get('/booking-holds/my'),
};

// Cart
export const cartAPI = {
  getCart: () => api.get('/cart'),
  addToCart: (data) => api.post('/cart/add', data),
  removeFromCart: (itemId) => api.delete(`/cart/item/${itemId}`),
  clearCart: () => api.delete('/cart/clear'),
  checkout: () => api.post('/cart/checkout'),
  refreshHolds: () => api.post('/cart/refresh-holds'),
};

// Notifications
export const notificationsAPI = {
  getNotifications: (limit = 50, unreadOnly = false) => 
    api.get('/notifications', { params: { limit, unread_only: unreadOnly } }),
  getUnreadCount: () => api.get('/notifications/unread-count'),
  markAsRead: (notificationId) => api.put(`/notifications/${notificationId}/read`),
  markAllAsRead: () => api.put('/notifications/read-all'),
  deleteNotification: (notificationId) => api.delete(`/notifications/${notificationId}`),
  clearAll: () => api.delete('/notifications'),
};

// Payment Gateways
export const walletAPI = {
  me: () => api.get('/wallet/me'),
  bookCart: () => api.post('/wallet/book-cart'),
};

export const subscriptionAPI = {
  refundEligibility: () => api.get('/subscriptions/me/refund-eligibility'),
  requestRefund: (reason) => api.post('/subscriptions/me/refund-request', { reason }),
  plans: () => api.get('/subscriptions/plans'),
  me: () => api.get('/subscriptions/me'),
  start: (choice) => api.post('/subscriptions', choice),
  change: (choice) => api.post('/subscriptions/me/change', choice),
  cancel: () => api.post('/subscriptions/me/cancel'),
  resume: () => api.post('/subscriptions/me/resume'),
  pay: () => api.post('/subscriptions/me/pay'),
  bookCart: () => api.post('/subscriptions/me/book-cart'),
};

export const paymentAPI = {
  getPricing: () => api.get('/pricing'),
  // Gateway info
  getGateways: () => api.get('/payment/gateways'),
  getGatewayStatus: () => api.get('/payment/gateway-status'),
  
  // Xendit (Philippines)
  xenditCheckout: () => api.post('/payment/xendit/checkout'),
  getXenditStatus: (externalId) => api.get(`/payment/xendit/status/${externalId}`),
  xenditBookingCheckout: (bookingId) => api.post(`/payment/xendit/booking/${bookingId}/checkout`),
  getRegion: () => api.get('/payment/region'),
  options: () => api.get('/payment/options'),
  wiseTransfer: (id) => api.get(`/payment/wise/${id}`),
  wiseMine: () => api.get('/payment/wise/mine'),
  wiseCurrency: (id, currency) => api.put(`/payment/wise/${id}/currency`, { currency }),
  wiseSent: (id) => api.post(`/payment/wise/${id}/sent`),
  wiseCancel: (id) => api.post(`/payment/wise/${id}/cancel`),
  wiseReceiptUpload: (id, file, onUploadProgress) => {
    const form = new FormData();
    form.append('file', file);
    return api.post(`/payment/wise/${id}/receipt`, form, { headers: { 'Content-Type': 'multipart/form-data' }, onUploadProgress });
  },
  wiseReceiptFile: (id, receiptId) => api.get(`/payment/wise/${id}/receipts/${receiptId}`, { responseType: 'blob' }),
  wiseReceiptDelete: (id, receiptId) => api.delete(`/payment/wise/${id}/receipts/${receiptId}`),
  setRegion: (region) => api.put('/payment/region', { region }),
  
  // WISE (International Payouts)
  getWiseAccountRequirements: (sourceCurrency = 'PHP', targetCurrency = 'USD') =>
    api.get('/wise/account-requirements', { params: { source_currency: sourceCurrency, target_currency: targetCurrency } }),
  createWiseRecipient: (data) => api.post('/wise/recipients', data),
  getWiseRecipients: () => api.get('/wise/recipients'),
  deleteWiseRecipient: (id) => api.delete(`/wise/recipients/${id}`),
  createWiseQuote: (sourceCurrency, targetCurrency, targetAmount) =>
    api.post('/wise/quote', null, { params: { source_currency: sourceCurrency, target_currency: targetCurrency, target_amount: targetAmount } }),
  processWisePayout: (data) => api.post('/wise/payout', data),
  getWiseBalance: () => api.get('/wise/balance'),
  getWiseTransferStatus: (transferId) => api.get(`/wise/transfer/${transferId}`),
  
  // Admin payout settings
  getPayoutSettings: () => api.get('/admin/payout-settings'),
  updatePayoutSettings: (settings) => api.put('/admin/payout-settings', settings),
};

// Add email settings methods to adminAPI
adminAPI.getEmailSettings = () => api.get('/admin/email-settings');
adminAPI.updateEmailSettings = (settings) => api.put('/admin/email-settings', settings);
adminAPI.sendTestEmail = (notificationType) => 
  api.post('/admin/email-settings/test', null, { params: { notification_type: notificationType } });
adminAPI.getNotificationTypes = () => api.get('/admin/email-settings/notification-types');


// Messaging API
export const messagingAPI = {
  getConversations: () => api.get('/messages/conversations'),
  getMessages: (conversationId, limit = 50) => api.get(`/messages/conversations/${conversationId}`, { params: { limit } }),
  sendMessage: (data) => api.post('/messages/send', data),
  sendTyping: (conversationId) => api.post('/messages/typing', `conversation_id=${conversationId}`, { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }),
  markRead: (conversationId) => api.post(`/messages/read/${conversationId}`),
  getUnreadCount: () => api.get('/messages/unread-count'),
  getOnlineStatusSnapshot: () => api.get('/messages/online-status'),
  getOnlineStatus: (userId) => api.get(`/messages/online-status/${userId}`),
  getContacts: () => api.get('/messages/contacts'),
  // Admin
  adminGetConversations: () => api.get('/admin/messages/conversations'),
  adminGetMessages: (conversationId) => api.get(`/admin/messages/conversations/${conversationId}`),
};

export default api;
// Speaking entry (placement) test
export const entryTestAPI = {
  questions: () => api.get('/entry-test/questions'),
  me: () => api.get('/entry-test/me'),
  saveAnswer: (questionIndex, blob, durationSeconds) => {
    const form = new FormData();
    const ext = (blob.type || '').includes('mp4') ? 'm4a' : (blob.type || '').includes('ogg') ? 'ogg' : 'webm';
    form.append('question_index', String(questionIndex));
    form.append('duration_seconds', String(durationSeconds || 0));
    form.append('file', blob, `answer-${questionIndex + 1}.${ext}`);
    return api.post('/entry-test/answers', form, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  submit: () => api.post('/entry-test/submit', {}),
  settings: () => api.get('/admin/entry-test-settings'),
  updateSettings: (mode) => api.put('/admin/entry-test-settings', { mode }),
  rescore: (testId) => api.post(`/entry-tests/${testId}/ai-score`),
  list: (params = {}) => api.get('/entry-tests', { params }),
  audio: (testId, index) => api.get(`/entry-tests/${testId}/answers/${index}/audio`, { responseType: 'blob' }),
  review: (testId, level, notes) => api.patch(`/entry-tests/${testId}/review`, { level, notes }),
};

// Teacher applications (applicants) and onboarding (admin)
export const applicationsAPI = {
  me: () => api.get('/applications/me'),
  save: (data) => api.put('/applications/me', data),
  submit: () => api.post('/applications/me/submit'),
  adminList: (stage) => api.get('/admin/applications', { params: stage ? { stage } : {} }),
  setStage: (id, data) => api.post(`/admin/applications/${id}/stage`, data),
  message: (id, text) => api.post(`/admin/applications/${id}/message`, { text }),
  addNote: (id, text) => api.post(`/admin/applications/${id}/notes`, { text }),
  activate: (id, data) => api.post(`/admin/applications/${id}/activate`, data),
};

export const classroomAPI = {
  practice: () => api.get('/classroom/practice'),
};
